import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { FileSizePipe } from '@pipes/file-size.pipe';

import {
    Papa,
    PapaParseConfig,
    PapaParseMeta,
} from 'ngx-papaparse';
import {
    Observable,
    of,
    throwError,
} from 'rxjs';
import { mergeMap } from 'rxjs/operators';

const MAX_CSV_FILE_SIZE = 50 * 1024 * 1024; // TODO:  50MB set on actual magic number

export enum ICSVParseErrorType {
    Quotes = 'Quotes',
    Delimiter = 'Delimiter',
    FieldMismatch = 'FieldMismatch',
    FieldError = 'FieldError',
}

export enum ICSVParseErrorCode {
    MissingQuotes = 'MissingQuotes',
    UndetectableDelimiter = 'UndetectableDelimiter',
    TooFewFields = 'TooFewFields',
    TooManyFields = 'TooManyFields',
    InvalidQuotes = 'InvalidQuotes',
    InvalidData = 'InvalidData',
    MissingData = 'MissingData',
}

export interface ICSVParseError<T> {
    row: number;
    type: ICSVParseErrorType;
    field?: T;
    code: ICSVParseErrorCode;
}

export interface ICSVParseResult<T> {
    errors: ICSVParseError<T>[];
    data: any;
    meta: PapaParseMeta;
}

export interface ICSVParseConfig extends PapaParseConfig {
    maxFileSize?: number;
    maxRows?: number;
}

@Injectable({
    providedIn: 'root',
})
export class CSVParser<T> {
    constructor(
        private _parseService: Papa,
        private _translateService: TranslateService,
        private _fileSizePipe: FileSizePipe,
    ) {
    }

    parseFile(file: File, config?: ICSVParseConfig) {
        const maxSize = config ?
            (config.maxFileSize || MAX_CSV_FILE_SIZE) :
            MAX_CSV_FILE_SIZE;
        if (file.size > maxSize) {
            return throwError(
                this._translateService.instant(`file_size_too_large`, {
                    maxSize: this._fileSizePipe.transform(maxSize, 0),
                }),
            );
        }

        return new Observable<ICSVParseResult<T>>(
            subscriber => {
                this._parseService.parse(file, {
                    complete: (response) => {
                        subscriber.next(response as ICSVParseResult<T>);
                        subscriber.complete();
                    },
                    error: (err) => subscriber.error(err.toString()),
                    ...config,
                });

                return () => { subscriber.complete(); };
            },
        ).pipe(
            mergeMap((result: ICSVParseResult<T>) => {
                const maxRows = !!config && config.maxRows || 0;
                return maxRows && result.data.length > maxRows ?
                    throwError(this._translateService.instant(`csv_parse_too_many_records`, {
                        records: result.data.length,
                        max: maxRows,
                    })) :
                    of(result);
            }),
        );
    }

    generateFile(items: any[]) {
        return this._parseService.unparse(
            items,
            {
                delimiter: ';',
            },
        );
    }
}
